import axios from "axios";
import i18n from "../../i18n/config";
import * as tokenStorage from "./tokenStorage";

// Defaults to the local backend. When REACT_APP_API_BASE_URL is set to an empty
// string (e.g. for localtunnel testing), calls become same-origin relative paths
// that CRA's dev proxy forwards to the backend.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "https://api.theuniflow.com";

const uniAdminApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

let uniAdminNotificationHandler = null;

export const setUniAdminNotificationHandler = (handler) => {
  uniAdminNotificationHandler = handler;
};

export const translateErrorCode = (errorCode) => {
  if (!errorCode) return i18n.t("errors:GENERIC_ERROR");
  const translated = i18n.t(`errors:${errorCode}`, { defaultValue: null });
  return translated || i18n.t("errors:GENERIC_ERROR");
};

// Refresh token queue management
let isRefreshing = false;
let failedRequestQueue = [];

const processQueue = (error, token = null) => {
  failedRequestQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedRequestQueue = [];
};

uniAdminApi.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const language = i18n.language || window.localStorage.getItem("i18nextLng") || "en";
    config.headers["Accept-Language"] = language;

    // Let the browser set the correct multipart/form-data boundary for file uploads
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error),
);

uniAdminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      const { status, data } = error.response;

      if (status === 401 && !originalRequest._retry) {
        const refreshToken = tokenStorage.getRefreshToken();

        if (!refreshToken) {
          tokenStorage.clearTokens();
          window.location.href = "/uni-admin/login";
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedRequestQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return uniAdminApi(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const res = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
            refreshToken,
          });
          const newAccessToken = res.data?.data?.accessToken;
          const newRefreshToken = res.data?.data?.refreshToken;

          tokenStorage.updateTokens(newAccessToken, newRefreshToken);

          processQueue(null, newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return uniAdminApi(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          tokenStorage.clearTokens();
          window.location.href = "/uni-admin/login";
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // 403s are a normal, expected outcome now that admins can hold partial
      // grants (a stale UI gate, a race after a Super Admin revokes access
      // mid-session, etc.) — not a sign the session itself is invalid, so we
      // must not log the user out. The backend's errorCode for these is
      // "permission.denied", which already resolves via errors:permission.denied
      // below to a generic "you don't have permission" toast.
      // The login form shows its own (translated) error message inline, so
      // skip the global toast here to avoid displaying the same failure twice.
      const isLoginRequest = originalRequest?.url?.includes("/university-admin/login");

      if (!isLoginRequest) {
        const errorCode = data?.errorCode;
        const errorMessage = translateErrorCode(errorCode || "GENERIC_ERROR");

        if (uniAdminNotificationHandler) {
          uniAdminNotificationHandler.showError(errorMessage);
        }
      }
    } else if (error.request) {
      // A transient network hiccup (e.g. a slow cold start in the WhatsApp
      // in-app browser) must not hard-reload the SPA away from a login or
      // auth-bootstrap state that hasn't resolved yet -- surface a toast via
      // the same notification handler as every other error instead of
      // forcing navigation with window.location.href.
      if (uniAdminNotificationHandler) {
        uniAdminNotificationHandler.showError(translateErrorCode("NETWORK_ERROR"));
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Upload a photo using the university-admin token.
 * Uses uniAdminApi so the correct Bearer token is attached.
 */
export async function uploadPhoto(file) {
  const filename =
    file.name && /\.[a-zA-Z0-9]+$/.test(file.name)
      ? file.name
      : `upload.${(file.type.split("/")[1] || "jpg").toLowerCase()}`;

  const formData = new FormData();
  formData.append("file", file, filename);

  const response = await uniAdminApi.post("/api/photos/upload", formData);
  return response.data;
}

export default uniAdminApi;
